import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import _ from 'lodash';

import { Checkbox } from '@/core/Checkbox.atom';

export type RadioButtonOption = {
  id: string;
  label: string | React.ReactNode;
  checked: boolean;
  onToggle: () => void;
  tooltip?: string;
  isTooltipSimple?: boolean;
  description?: string;
  disabled?: boolean;
};

type RadioButtonGroupProps = {
  options: RadioButtonOption[];
  gridLayout?: boolean;
  verticalLayout?: boolean;
  xstyle?: stylex.StyleXStyles;
  checkboxXstyle?: stylex.StyleXStyles;
  checkboxLayoutXstyle?: stylex.StyleXStyles;
  extraClassNames?: string;
  checkboxExtraClassNames?: string;
  onChange?: (option?: RadioButtonOption) => void;
  testId?: string;
};

/** Manage and display a group of radio buttons in a grid, vertical or horizontal (default) layout */
export const RadioButtonGroup = ({
  options,
  gridLayout = false,
  verticalLayout,
  extraClassNames,
  xstyle,
  checkboxXstyle,
  checkboxLayoutXstyle,
  checkboxExtraClassNames,
  onChange,
  testId,
}: RadioButtonGroupProps) => {
  return (
    <div
      data-testid={testId}
      {...stylex.props(
        styles.group,
        verticalLayout ? styles.vertical : styles.horizontal,
        gridLayout && styles.grid,
        xstyle,
      )}
      className={clsx(
        stylex.props(
          styles.group,
          verticalLayout ? styles.vertical : styles.horizontal,
          gridLayout && styles.grid,
          xstyle,
        ).className,
        extraClassNames,
      )}>
      {_.map(options, (option) => (
        <Checkbox
          key={`${option.id}_checkbox`}
          classes={checkboxExtraClassNames}
          layoutXstyle={checkboxLayoutXstyle}
          xstyle={[
            styles.option,
            gridLayout && styles.gridOption,
            verticalLayout && styles.verticalOption,
            checkboxXstyle,
          ]}
          type="radio"
          id={option.id}
          label={option.label}
          isChecked={option.checked}
          disabled={option.disabled}
          tooltip={option.tooltip}
          isTooltipSimple={option.isTooltipSimple}
          description={option.description}
          value={option.id}
          onChange={() => {
            onChange?.(option);
            option.onToggle();
          }}
        />
      ))}
    </div>
  );
};

const styles = stylex.create({
  group: { display: 'flex' },
  horizontal: { flexDirection: 'row', alignItems: 'center' },
  vertical: { flexDirection: 'column' },
  grid: { flexWrap: 'wrap' },
  option: { marginRight: '.9375rem' },
  gridOption: { width: 100 },
  verticalOption: { marginBottom: 0 },
});
